// src/GDB/pages/CSVDatasetsPage.jsx
/**
 * CSV Datasets Management Page V2
 * 
 * This version creates REAL MySQL tables from CSV files,
 * allowing full SQL query support!
 */

import { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../api/client";

export default function CSVDatasetsPage() {
  const [datasets, setDatasets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Selected dataset state
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetData, setDatasetData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  
  // SQL Query editor
  const [editingQuery, setEditingQuery] = useState("");
  const [queryResult, setQueryResult] = useState(null);
  const [runningQuery, setRunningQuery] = useState(false);

  // Load datasets
  const loadDatasets = async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await apiGet("/csv-datasets/");
      setDatasets(data || []);
    } catch (err) {
      setError("Failed to load datasets: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDatasets();
  }, []);

  // Handle file selection and preview
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadFile(file);
    setUploadName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension
    setUploadPreview(null);
    
    // Get preview
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const preview = await apiPost("/csv-datasets/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadPreview(preview);
    } catch (err) {
      setError("Failed to preview CSV: " + err.message);
    }
  };

  // Upload CSV
  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) {
      setError("Please select a file and enter a name");
      return;
    }
    
    setUploading(true);
    setError("");
    
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("name", uploadName.trim());
    
    try {
      const result = await apiPost("/csv-datasets/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setSuccess(`✅ Created dataset "${result.name}" with table "${result.table_name}"`);
      setShowUpload(false);
      setUploadFile(null);
      setUploadName("");
      setUploadPreview(null);
      loadDatasets();
      
      // Auto-select the new dataset
      handleViewDataset(result);
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  // View dataset details
  const handleViewDataset = async (dataset) => {
    setSelectedDataset(dataset);
    setLoadingData(true);
    setDatasetData(null);
    setQueryResult(null);
    
    try {
      const data = await apiGet(`/csv-datasets/${dataset.id || dataset.dataset_id}`);
      setDatasetData(data);
      setEditingQuery(data.sql_query || "");
    } catch (err) {
      setError("Failed to load dataset: " + err.message);
    } finally {
      setLoadingData(false);
    }
  };

  // Run/Test SQL query
  const handleRunQuery = async () => {
    if (!editingQuery.trim() || !selectedDataset) return;
    
    setRunningQuery(true);
    setQueryResult(null);
    
    try {
      // Use the query endpoint to test - FIX: trim the query to avoid trailing whitespace issues
      const result = await apiGet(`/query/dataset/${selectedDataset.dataset_id || selectedDataset.id}?test_query=${encodeURIComponent(editingQuery.trim())}`);
      setQueryResult({
        success: true,
        data: result.data || [],
        columns: result.columns || Object.keys(result.data?.[0] || {}),
      });
    } catch (err) {
      setQueryResult({
        success: false,
        error: err.message,
      });
    } finally {
      setRunningQuery(false);
    }
  };

  // Save SQL query
  const handleSaveQuery = async () => {
    if (!editingQuery.trim() || !selectedDataset) return;
    
    try {
      const formData = new FormData();
      formData.append("sql_query", editingQuery.trim()); // Also trim when saving
      
      await apiPut(`/csv-datasets/${selectedDataset.dataset_id || selectedDataset.id}/query`, formData);
      setSuccess("✅ Query saved!");
      
      // Refresh dataset
      handleViewDataset(selectedDataset);
    } catch (err) {
      setError("Failed to save query: " + err.message);
    }
  };

  // Delete dataset
  const handleDelete = async (dataset) => {
    if (!window.confirm(`Delete "${dataset.name}"? This will also delete the MySQL table.`)) {
      return;
    }
    
    try {
      await apiDelete(`/csv-datasets/${dataset.id || dataset.dataset_id}`);
      setSuccess("🗑️ Dataset deleted");
      loadDatasets();
      if (selectedDataset?.id === dataset.id) {
        setSelectedDataset(null);
        setDatasetData(null);
      }
    } catch (err) {
      setError("Delete failed: " + err.message);
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📁 CSV Datasets</h1>
          <p style={styles.subtitle}>
            Upload CSV files → Creates MySQL tables → Full SQL query support!
          </p>
        </div>
        <button onClick={() => setShowUpload(true)} style={styles.uploadBtn}>
          + Upload CSV
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={styles.errorMsg}>
          ❌ {error}
          <button onClick={() => setError("")} style={styles.dismissBtn}>×</button>
        </div>
      )}
      {success && (
        <div style={styles.successMsg}>
          {success}
          <button onClick={() => setSuccess("")} style={styles.dismissBtn}>×</button>
        </div>
      )}

      {/* Main Content */}
      <div style={styles.content}>
        {/* Dataset List */}
        <div style={styles.listSection}>
          <h2 style={styles.sectionTitle}>Datasets ({datasets.length})</h2>
          
          {isLoading ? (
            <div style={styles.loading}>Loading...</div>
          ) : datasets.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📄</div>
              <p>No CSV datasets yet</p>
              <button onClick={() => setShowUpload(true)} style={styles.emptyBtn}>
                Upload CSV
              </button>
            </div>
          ) : (
            <div style={styles.datasetList}>
              {datasets.map((ds) => (
                <div
                  key={ds.id}
                  style={{
                    ...styles.datasetCard,
                    ...(selectedDataset?.id === ds.id ? styles.selectedCard : {}),
                  }}
                  onClick={() => handleViewDataset(ds)}
                >
                  <div style={styles.cardHeader}>
                    <span style={styles.cardIcon}>📊</span>
                    <span style={styles.cardName}>{ds.name}</span>
                  </div>
                  <div style={styles.cardMeta}>
                    <span>🗄️ {ds.table_name}</span>
                    <span>📝 {ds.row_count} rows</span>
                  </div>
                  <div style={styles.cardActions}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(ds); }}
                      style={styles.deleteBtn}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dataset Detail / SQL Editor */}
        {selectedDataset && (
          <div style={styles.detailSection}>
            <h2 style={styles.detailTitle}>
              📊 {selectedDataset.name}
            </h2>
            
            {loadingData ? (
              <div style={styles.loading}>Loading...</div>
            ) : datasetData && (
              <>
                {/* Info */}
                <div style={styles.infoGrid}>
                  <div style={styles.infoCard}>
                    <span style={styles.infoLabel}>MySQL Table (Full Name)</span>
                    <code style={styles.infoValue}>{datasetData.full_table_name || datasetData.table_name}</code>
                  </div>
                  <div style={styles.infoCard}>
                    <span style={styles.infoLabel}>Dataset ID</span>
                    <code style={styles.infoValue}>{datasetData.id}</code>
                  </div>
                  <div style={styles.infoCard}>
                    <span style={styles.infoLabel}>Rows</span>
                    <span style={styles.infoValue}>{datasetData.row_count}</span>
                  </div>
                </div>

                {/* Columns */}
                <div style={styles.columnsSection}>
                  <h4>📋 Available Columns</h4>
                  <div style={styles.columnsList}>
                    {datasetData.columns?.map((col) => (
                      <span key={col} style={styles.columnBadge}>
                        {col}
                        <span style={styles.columnType}>
                          {datasetData.column_types?.[col] || ""}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>

                {/* SQL Query Editor */}
                <div style={styles.querySection}>
                  <h4>✏️ SQL Query (customize what this dataset returns)</h4>
                  <textarea
                    value={editingQuery}
                    onChange={(e) => setEditingQuery(e.target.value)}
                    style={styles.queryEditor}
                    placeholder={`SELECT * FROM ${datasetData.full_table_name || datasetData.table_name} LIMIT 1000`}
                    rows={6}
                  />
                  <div style={styles.queryActions}>
                    <button
                      onClick={handleRunQuery}
                      disabled={runningQuery}
                      style={styles.runBtn}
                    >
                      {runningQuery ? "Running..." : "▶️ Run Query"}
                    </button>
                    <button onClick={handleSaveQuery} style={styles.saveBtn}>
                      💾 Save Query
                    </button>
                  </div>
                  
                  {/* Query examples */}
                  <div style={styles.queryExamples}>
                    <strong>Example queries:</strong>
                    <pre style={styles.exampleCode}>
{`-- Count by a column
SELECT status, COUNT(*) as count 
FROM ${datasetData.full_table_name || datasetData.table_name}
GROUP BY status

-- Sum values
SELECT assignee, SUM(duration) as total 
FROM ${datasetData.full_table_name || datasetData.table_name}
GROUP BY assignee

-- Filter data
SELECT * FROM ${datasetData.full_table_name || datasetData.table_name}
WHERE priority = 'High'`}
                    </pre>
                  </div>
                </div>

                {/* Query Result */}
                {queryResult && (
                  <div style={styles.resultSection}>
                    <h4>📊 Query Result</h4>
                    {queryResult.success ? (
                      <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              {queryResult.columns?.map((col) => (
                                <th key={col} style={styles.th}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {queryResult.data?.slice(0, 20).map((row, idx) => (
                              <tr key={idx}>
                                {queryResult.columns?.map((col) => (
                                  <td key={col} style={styles.td}>{String(row[col] ?? "")}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {queryResult.data?.length > 20 && (
                          <div style={styles.moreRows}>
                            Showing 20 of {queryResult.data.length} rows
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={styles.queryError}>
                        ❌ {queryResult.error}
                      </div>
                    )}
                  </div>
                )}

                {/* Usage hint */}
                <div style={styles.usageHint}>
                  <strong>💡 How to use in dashboards:</strong>
                  <ol style={{ margin: "8px 0 0 20px", padding: 0 }}>
                    <li>Go to <strong>Datasets</strong> page</li>
                    <li>This dataset (ID: {datasetData.id}) is already there!</li>
                    <li>The SQL query above defines what data the widgets will see</li>
                    <li>Create a widget and select this dataset</li>
                  </ol>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div style={styles.modalBackdrop} onClick={() => setShowUpload(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>📤 Upload CSV File</h2>
            
            <div style={styles.formGroup}>
              <label>Select CSV File</label>
              <input
                type="file"
                accept=".csv,.tsv,.txt"
                onChange={handleFileSelect}
                style={styles.fileInput}
              />
            </div>
            
            {uploadPreview && (
              <>
                <div style={styles.previewInfo}>
                  <span>📊 {uploadPreview.total_rows} rows</span>
                  <span>📋 {uploadPreview.columns.length} columns</span>
                  <span>🗄️ Table: {uploadPreview.suggested_table_name}</span>
                </div>
                
                <div style={styles.formGroup}>
                  <label>Dataset Name</label>
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    style={styles.textInput}
                    placeholder="My CSV Dataset"
                  />
                </div>
                
                <div style={styles.previewSection}>
                  <h4>Column Types (auto-detected)</h4>
                  <div style={styles.columnsList}>
                    {uploadPreview.columns.map((col) => (
                      <span key={col} style={styles.columnBadge}>
                        {col}
                        <span style={styles.columnType}>
                          {uploadPreview.detected_types[col]}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
                
                <div style={styles.previewSection}>
                  <h4>Sample Data</h4>
                  <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          {uploadPreview.columns.map((col) => (
                            <th key={col} style={styles.th}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uploadPreview.sample_rows.slice(0, 5).map((row, idx) => (
                          <tr key={idx}>
                            {uploadPreview.columns.map((col) => (
                              <td key={col} style={styles.td}>{String(row[col] ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            
            <div style={styles.modalActions}>
              <button onClick={() => setShowUpload(false)} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadFile || !uploadName.trim()}
                style={styles.uploadSubmitBtn}
              >
                {uploading ? "Creating Table..." : "Create MySQL Table & Dataset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const styles = {
  page: { padding: "24px", maxWidth: "1400px", margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", borderBottom: "1px solid #e5e7eb", paddingBottom: "20px" },
  title: { margin: 0, fontSize: "1.75rem", fontWeight: 700 },
  subtitle: { margin: "8px 0 0", color: "#6b7280", fontSize: "0.95rem" },
  uploadBtn: { padding: "12px 24px", backgroundColor: "#3b82f6", color: "#fff", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer" },
  errorMsg: { padding: "12px 16px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#dc2626", marginBottom: "16px", display: "flex", justifyContent: "space-between" },
  successMsg: { padding: "12px 16px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", color: "#16a34a", marginBottom: "16px", display: "flex", justifyContent: "space-between" },
  dismissBtn: { background: "none", border: "none", fontSize: "20px", cursor: "pointer" },
  content: { display: "flex", gap: "24px" },
  listSection: { flex: "0 0 320px", backgroundColor: "#f9fafb", borderRadius: "12px", padding: "20px", maxHeight: "calc(100vh - 220px)", overflowY: "auto" },
  sectionTitle: { margin: "0 0 16px", fontSize: "1.1rem", fontWeight: 600 },
  loading: { padding: "20px", textAlign: "center", color: "#6b7280" },
  emptyState: { textAlign: "center", padding: "40px 20px", color: "#6b7280" },
  emptyIcon: { fontSize: "48px", marginBottom: "16px" },
  emptyBtn: { marginTop: "16px", padding: "10px 20px", backgroundColor: "#3b82f6", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" },
  datasetList: { display: "flex", flexDirection: "column", gap: "12px" },
  datasetCard: { backgroundColor: "#fff", borderRadius: "8px", padding: "16px", border: "1px solid #e5e7eb", cursor: "pointer", transition: "all 0.2s" },
  selectedCard: { borderColor: "#3b82f6", boxShadow: "0 0 0 2px rgba(59,130,246,0.2)" },
  cardHeader: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" },
  cardIcon: { fontSize: "20px" },
  cardName: { fontWeight: 600, fontSize: "1rem" },
  cardMeta: { display: "flex", gap: "16px", fontSize: "0.8rem", color: "#6b7280" },
  cardActions: { marginTop: "12px", display: "flex", gap: "8px" },
  deleteBtn: { padding: "4px 8px", backgroundColor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "4px", cursor: "pointer" },
  detailSection: { flex: 1, backgroundColor: "#fff", borderRadius: "12px", padding: "24px", border: "1px solid #e5e7eb", maxHeight: "calc(100vh - 220px)", overflowY: "auto" },
  detailTitle: { margin: "0 0 20px", fontSize: "1.4rem", fontWeight: 700 },
  infoGrid: { display: "flex", gap: "16px", marginBottom: "20px" },
  infoCard: { backgroundColor: "#f9fafb", padding: "12px 16px", borderRadius: "8px", flex: 1 },
  infoLabel: { display: "block", fontSize: "0.75rem", color: "#6b7280", marginBottom: "4px" },
  infoValue: { fontSize: "1rem", fontWeight: 600 },
  columnsSection: { marginBottom: "20px" },
  columnsList: { display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" },
  columnBadge: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 12px", backgroundColor: "#e5e7eb", borderRadius: "20px", fontSize: "0.85rem" },
  columnType: { fontSize: "0.7rem", color: "#6b7280", backgroundColor: "#d1d5db", padding: "2px 6px", borderRadius: "4px" },
  querySection: { marginBottom: "20px", padding: "16px", backgroundColor: "#f9fafb", borderRadius: "8px" },
  queryEditor: { width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "6px", fontFamily: "monospace", fontSize: "0.9rem", resize: "vertical", marginTop: "8px" },
  queryActions: { display: "flex", gap: "12px", marginTop: "12px" },
  runBtn: { padding: "8px 16px", backgroundColor: "#10b981", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 500 },
  saveBtn: { padding: "8px 16px", backgroundColor: "#3b82f6", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 500 },
  queryExamples: { marginTop: "16px", fontSize: "0.85rem" },
  exampleCode: { backgroundColor: "#1f2937", color: "#e5e7eb", padding: "12px", borderRadius: "6px", overflow: "auto", fontSize: "0.8rem", marginTop: "8px" },
  resultSection: { marginBottom: "20px" },
  tableWrapper: { overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "8px", maxHeight: "300px", overflowY: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { padding: "10px 12px", textAlign: "left", backgroundColor: "#f3f4f6", fontWeight: 600, borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0 },
  td: { padding: "8px 12px", borderBottom: "1px solid #e5e7eb" },
  moreRows: { padding: "8px", textAlign: "center", color: "#6b7280", fontSize: "0.85rem", backgroundColor: "#f9fafb" },
  queryError: { padding: "12px", backgroundColor: "#fef2f2", color: "#dc2626", borderRadius: "6px" },
  usageHint: { padding: "16px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", fontSize: "0.9rem", color: "#92400e" },
  modalBackdrop: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modalContent: { backgroundColor: "#fff", borderRadius: "12px", padding: "24px", maxWidth: "800px", width: "90%", maxHeight: "90vh", overflowY: "auto" },
  modalTitle: { margin: "0 0 20px", fontSize: "1.25rem" },
  formGroup: { marginBottom: "16px" },
  fileInput: { display: "block", width: "100%", padding: "12px", border: "2px dashed #d1d5db", borderRadius: "8px", cursor: "pointer" },
  textInput: { width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "1rem" },
  previewInfo: { display: "flex", gap: "16px", padding: "12px", backgroundColor: "#f0fdf4", borderRadius: "8px", marginBottom: "16px" },
  previewSection: { marginBottom: "16px" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #e5e7eb" },
  cancelBtn: { padding: "10px 20px", backgroundColor: "#f3f4f6", color: "#374151", border: "none", borderRadius: "6px", cursor: "pointer" },
  uploadSubmitBtn: { padding: "10px 20px", backgroundColor: "#3b82f6", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 },
};